import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Users, Plus, Edit2, Trash2, Wallet, DollarSign, Percent, AlertTriangle, Eye, EyeOff, Share2, ArrowRightLeft, ListChecks } from 'lucide-react';
import { toast } from 'sonner';

export default function VendedoresPage() {
  const { api, user } = useAuthStore();
  const navigate = useNavigate();
  const [vendedores, setVendedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nexupayActive, setNexupayActive] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  // Share plans & Transfer customers
  const [sharePlansOpen, setSharePlansOpen] = useState(null); // vendedor object
  const [myPlans, setMyPlans] = useState([]);
  const [selectedPlanIds, setSelectedPlanIds] = useState([]);
  const [transferOpen, setTransferOpen] = useState(null); // vendedor object
  const [myCustomers, setMyCustomers] = useState([]);
  const [selectedCustomerIds, setSelectedCustomerIds] = useState([]);
  const [transferSearch, setTransferSearch] = useState('');

  const [newVendedor, setNewVendedor] = useState({
    username: '', password: '', commission_type: 'percent', commission_value: 10,
    commission_enabled: true, can_see_all_customers: false
  });

  const [editData, setEditData] = useState({
    commission_type: 'percent', commission_value: 0,
    commission_enabled: true, can_see_all_customers: false, active: true
  });

  useEffect(() => { fetchVendedores(); }, []);

  const fetchVendedores = async () => {
    try {
      setLoading(true);
      const r = await api.get('/vendedores');
      setVendedores(r.data.vendedores || []);
      setNexupayActive(r.data.nexupay_active || false);
    } catch (e) {
      toast.error('Erro ao carregar vendedores');
    } finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!newVendedor.username || !newVendedor.password) {
      toast.error('Preencha nome de usuario e senha');
      return;
    }
    try {
      await api.post('/vendedores', newVendedor);
      toast.success('Vendedor criado!');
      setCreateOpen(false);
      setNewVendedor({ username: '', password: '', commission_type: 'percent', commission_value: 10, commission_enabled: true, can_see_all_customers: false });
      fetchVendedores();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao criar vendedor');
    }
  };

  const handleUpdate = async () => {
    if (!editOpen) return;
    try {
      await api.put(`/vendedores/${editOpen.id}`, editData);
      toast.success('Vendedor atualizado!');
      setEditOpen(null);
      fetchVendedores();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao atualizar');
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await api.delete(`/vendedores/${deleteConfirm.id}`);
      toast.success('Vendedor excluido! Clientes transferidos para voce.');
      setDeleteConfirm(null);
      fetchVendedores();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao excluir');
    }
  };

  const openEdit = (v) => {
    setEditData({
      commission_type: v.commission_type || 'percent',
      commission_value: v.commission_value || 0,
      commission_enabled: v.commission_enabled ?? true,
      can_see_all_customers: v.can_see_all_customers ?? false,
      active: v.active ?? true
    });
    setEditOpen(v);
  };

  const openSharePlans = async (v) => {
    try {
      const r = await api.get('/customer-plans');
      setMyPlans(r.data.filter(p => p.user_id === user?.id));
      setSelectedPlanIds([]);
      setSharePlansOpen(v);
    } catch { toast.error('Erro ao carregar planos'); }
  };

  const handleSharePlans = async () => {
    if (!sharePlansOpen || selectedPlanIds.length === 0) return;
    try {
      const r = await api.post(`/vendedores/${sharePlansOpen.id}/share-plans`, { plan_ids: selectedPlanIds });
      toast.success(r.data.message);
      setSharePlansOpen(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };

  const openTransfer = async (v) => {
    try {
      const r = await api.get('/customers?limit=50000');
      setMyCustomers(r.data.filter(c => c.user_id === user?.id));
      setSelectedCustomerIds([]);
      setTransferSearch('');
      setTransferOpen(v);
    } catch { toast.error('Erro ao carregar clientes'); }
  };

  const handleTransfer = async () => {
    if (!transferOpen || selectedCustomerIds.length === 0) return;
    try {
      const r = await api.post(`/vendedores/${transferOpen.id}/transfer-customer`, { customer_ids: selectedCustomerIds });
      toast.success(r.data.message);
      setTransferOpen(null);
      fetchVendedores();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="vendedores-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Vendedores</h1>
          <p className="text-muted-foreground">{vendedores.length} vendedor{vendedores.length !== 1 ? 'es' : ''}</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="create-vendedor-btn">
          <Plus className="w-4 h-4 mr-2" /> Novo Vendedor
        </Button>
      </div>

      {!nexupayActive && (
        <Card className="p-4 border-amber-500/30 bg-amber-500/5">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-amber-500">NexuPay nao esta ativo</p>
              <p className="text-xs text-muted-foreground mt-1">
                As comissoes dos vendedores so funcionam com o NexuPay ativado. Ative o NexuPay em Gateways para habilitar comissoes.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card className="p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total</p>
              <p className="text-2xl font-bold">{vendedores.length}</p>
            </div>
            <Users className="w-8 h-8 text-primary opacity-50" />
          </div>
        </Card>
        <Card className="p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Clientes (total)</p>
              <p className="text-2xl font-bold text-blue-500">{vendedores.reduce((s, v) => s + (v.customers_count || 0), 0)}</p>
            </div>
            <Users className="w-8 h-8 text-blue-500 opacity-50" />
          </div>
        </Card>
        <Card className="p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Comissoes ativas</p>
              <p className="text-2xl font-bold text-emerald-500">{vendedores.filter(v => v.commission_enabled).length}</p>
            </div>
            <Wallet className="w-8 h-8 text-emerald-500 opacity-50" />
          </div>
        </Card>
      </div>

      {/* Vendedores List */}
      <div className="space-y-3">
        {vendedores.length === 0 ? (
          <Card className="p-12 text-center">
            <Users className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
            <p className="text-muted-foreground">Nenhum vendedor cadastrado</p>
            <p className="text-sm text-muted-foreground/70 mt-1">Crie vendedores para dividir comissoes das vendas</p>
          </Card>
        ) : vendedores.map(v => (
          <Card key={v.id} className={`p-4 ${!v.active ? 'opacity-50' : ''}`} data-testid={`vendedor-${v.id}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/15 text-primary flex items-center justify-center font-bold text-sm">
                  {v.username?.[0]?.toUpperCase() || 'V'}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{v.username}</span>
                    <Badge variant="outline" className="text-[10px] bg-indigo-500/15 text-indigo-400 border-indigo-500/20">
                      VENDEDOR
                    </Badge>
                    {!v.active && <Badge variant="destructive" className="text-[10px]">INATIVO</Badge>}
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                    <button type="button" className="hover:text-foreground underline-offset-2 hover:underline" onClick={() => navigate(`/vendedores/${v.id}/clientes`)}>{v.customers_count || 0} clientes</button>
                    <span>NexuPay: R$ {(v.nexupay_balance || 0).toFixed(2)}</span>
                    {v.commission_enabled ? (
                      <span className="text-emerald-500">
                        Comissao: {v.commission_type === 'percent' ? `${v.commission_value}%` : `R$ ${(v.commission_value || 0).toFixed(2)}`}
                      </span>
                    ) : (
                      <span className="text-muted-foreground/50">Comissao desativada</span>
                    )}
                    {v.can_see_all_customers && <span className="text-blue-400">Ve todos clientes</span>}
                  </div>
                </div>
              </div>
              <div className="flex gap-1.5 flex-wrap">
                <Button size="sm" className="h-8 text-xs gap-1" onClick={() => navigate(`/vendedores/${v.id}/clientes`)} title="Ver e gerenciar clientes e planos deste vendedor" data-testid={`view-vendedor-customers-${v.id}`}>
                  <ListChecks className="w-3.5 h-3.5" /> Ver clientes
                </Button>
                <Button variant="outline" size="sm" className="h-8 text-xs gap-1" onClick={() => openSharePlans(v)} title="Compartilhar planos">
                  <Share2 className="w-3.5 h-3.5" /> Planos
                </Button>
                <Button variant="outline" size="sm" className="h-8 text-xs gap-1" onClick={() => openTransfer(v)} title="Transferir clientes">
                  <ArrowRightLeft className="w-3.5 h-3.5" /> Clientes
                </Button>
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => openEdit(v)} data-testid={`edit-vendedor-${v.id}`}>
                  <Edit2 className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="icon" className="h-8 w-8 border-red-500/30 text-red-500 hover:bg-red-500/10"
                  onClick={() => setDeleteConfirm(v)} data-testid={`delete-vendedor-${v.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Novo Vendedor</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Nome de usuario</Label>
              <Input value={newVendedor.username} onChange={e => setNewVendedor({...newVendedor, username: e.target.value})}
                placeholder="vendedor1" data-testid="new-vendedor-username" />
            </div>
            <div>
              <Label className="text-xs">Senha</Label>
              <div className="relative">
                <Input type={showPassword ? 'text' : 'password'} value={newVendedor.password}
                  onChange={e => setNewVendedor({...newVendedor, password: e.target.value})}
                  placeholder="Senha segura" data-testid="new-vendedor-password" />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Tipo de Comissao</Label>
                <Select value={newVendedor.commission_type} onValueChange={v => setNewVendedor({...newVendedor, commission_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Porcentagem (%)</SelectItem>
                    <SelectItem value="fixed">Valor Fixo (R$)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Valor</Label>
                <div className="relative">
                  <Input type="number" min="0" step="0.01" value={newVendedor.commission_value}
                    onChange={e => setNewVendedor({...newVendedor, commission_value: parseFloat(e.target.value) || 0})}
                    data-testid="new-vendedor-commission" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                    {newVendedor.commission_type === 'percent' ? '%' : 'R$'}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">Comissao ativada</Label>
              <Switch checked={newVendedor.commission_enabled}
                onCheckedChange={v => setNewVendedor({...newVendedor, commission_enabled: v})} />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-xs">Ver clientes de outros vendedores</Label>
                <p className="text-[10px] text-muted-foreground">Permite ver clientes de todos os vendedores</p>
              </div>
              <Switch checked={newVendedor.can_see_all_customers}
                onCheckedChange={v => setNewVendedor({...newVendedor, can_see_all_customers: v})} />
            </div>
            {!nexupayActive && (
              <p className="text-xs text-amber-500 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> NexuPay inativo — comissoes nao serao aplicadas
              </p>
            )}
            <Button onClick={handleCreate} className="w-full" data-testid="confirm-create-vendedor">
              Criar Vendedor
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editOpen} onOpenChange={() => setEditOpen(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Editar {editOpen?.username}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Tipo de Comissao</Label>
                <Select value={editData.commission_type} onValueChange={v => setEditData({...editData, commission_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Porcentagem (%)</SelectItem>
                    <SelectItem value="fixed">Valor Fixo (R$)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Valor</Label>
                <div className="relative">
                  <Input type="number" min="0" step="0.01" value={editData.commission_value}
                    onChange={e => setEditData({...editData, commission_value: parseFloat(e.target.value) || 0})}
                    data-testid="edit-vendedor-commission" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                    {editData.commission_type === 'percent' ? '%' : 'R$'}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">Comissao ativada</Label>
              <Switch checked={editData.commission_enabled}
                onCheckedChange={v => setEditData({...editData, commission_enabled: v})} />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-xs">Ver clientes de outros vendedores</Label>
                <p className="text-[10px] text-muted-foreground">Permite ver clientes de todos os vendedores</p>
              </div>
              <Switch checked={editData.can_see_all_customers}
                onCheckedChange={v => setEditData({...editData, can_see_all_customers: v})} />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">Ativo</Label>
              <Switch checked={editData.active}
                onCheckedChange={v => setEditData({...editData, active: v})} />
            </div>
            {!nexupayActive && editData.commission_enabled && (
              <p className="text-xs text-amber-500 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> NexuPay inativo — comissoes nao serao aplicadas
              </p>
            )}
            <Button onClick={handleUpdate} className="w-full" data-testid="confirm-edit-vendedor">
              Salvar Alteracoes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle className="text-red-400">Excluir {deleteConfirm?.username}?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            O vendedor sera excluido e todos os seus clientes serao transferidos para voce.
          </p>
          <div className="flex gap-2 mt-4">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)} className="flex-1">Cancelar</Button>
            <Button variant="destructive" onClick={handleDelete} className="flex-1" data-testid="confirm-delete-vendedor">
              Excluir
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Share Plans Dialog */}
      <Dialog open={!!sharePlansOpen} onOpenChange={() => setSharePlansOpen(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Compartilhar Planos com {sharePlansOpen?.username}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Selecione os planos que deseja copiar para este vendedor:</p>
            {myPlans.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">Voce nao tem planos criados</p>
            ) : (
              <div className="max-h-60 overflow-y-auto space-y-2">
                {myPlans.map(p => (
                  <label key={p.id} className="flex items-center gap-3 p-2 rounded-lg border border-border/50 cursor-pointer hover:bg-muted/30">
                    <input type="checkbox" checked={selectedPlanIds.includes(p.id)}
                      onChange={e => {
                        if (e.target.checked) setSelectedPlanIds([...selectedPlanIds, p.id]);
                        else setSelectedPlanIds(selectedPlanIds.filter(id => id !== p.id));
                      }}
                      className="rounded" />
                    <div className="flex-1">
                      <span className="text-sm font-medium">{p.name}</span>
                      <span className="text-xs text-muted-foreground ml-2">R$ {(p.price || 0).toFixed(2)} — {p.duration_months || 1} mes(es)</span>
                    </div>
                  </label>
                ))}
              </div>
            )}
            <Button onClick={handleSharePlans} disabled={selectedPlanIds.length === 0} className="w-full">
              Compartilhar {selectedPlanIds.length > 0 ? `(${selectedPlanIds.length})` : ''}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Transfer Customers Dialog */}
      <Dialog open={!!transferOpen} onOpenChange={() => setTransferOpen(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Transferir Clientes para {transferOpen?.username}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Selecione os clientes que deseja transferir. Os planos serao copiados automaticamente.</p>
            <Input
              placeholder="Buscar por nome ou telefone..."
              value={transferSearch || ''}
              onChange={e => setTransferSearch(e.target.value)}
              className="h-9"
            />
            {myCustomers.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">Voce nao tem clientes</p>
            ) : (
              <div className="max-h-60 overflow-y-auto space-y-2">
                {myCustomers
                  .filter(c => {
                    if (!transferSearch) return true;
                    const q = transferSearch.toLowerCase();
                    return (c.name || '').toLowerCase().includes(q) || (c.phone || '').includes(q);
                  })
                  .map(c => (
                  <label key={c.id} className="flex items-center gap-3 p-2 rounded-lg border border-border/50 cursor-pointer hover:bg-muted/30">
                    <input type="checkbox" checked={selectedCustomerIds.includes(c.id)}
                      onChange={e => {
                        if (e.target.checked) setSelectedCustomerIds([...selectedCustomerIds, c.id]);
                        else setSelectedCustomerIds(selectedCustomerIds.filter(id => id !== c.id));
                      }}
                      className="rounded" />
                    <div className="flex-1">
                      <span className="text-sm font-medium">{c.name}</span>
                      <span className="text-xs text-muted-foreground ml-2">{c.phone}</span>
                    </div>
                  </label>
                ))}
              </div>
            )}
            <Button onClick={handleTransfer} disabled={selectedCustomerIds.length === 0} className="w-full">
              Transferir {selectedCustomerIds.length > 0 ? `(${selectedCustomerIds.length})` : ''}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
